import dayjs from 'dayjs';
import isNil from 'lodash/isNil';
import { action, computed, makeObservable, observable, reaction } from 'mobx';

import { postRequest } from '../../services/api';
import { devError } from '../../utils/log';
import { createSeenList } from '../../utils/seenList';
import type { CoinType } from '../cointype/CoinTypeStore';
import type { FeatureFetchStatus } from '../feature-manager/types';
import type { RootStore } from '../RootStore';
import { EnumOpenDialog } from '../ui/types';

import {
  getDefaultTocTrophy,
  DEFAULT_BETWEEN_ROUND_TROPHY,
  DEFAULT_STARTS_IN_TROPHY,
  TOC_DATA_MAX_AGE_MS,
  TOC_STALE_COUNTDOWN_MAX_RETRIES,
  TOC_STALE_COUNTDOWN_MAX_RETRY_MS,
  TOC_STALE_COUNTDOWN_RETRY_MS,
} from './constants';
import { shouldShowEliminationSummary } from './tocAdvancement';
import { resolveTocBannerRiveUrl } from './tocBannerRive';
import { cancelTocDialogOpen, markTocDialogOpenRequested } from './tocDialogOpenTiming';
import { resolveTocMock, TOC_MOCK_ACKNOWLEDGED } from './tocMockFlags';
import { doesTocHoldTournamentSlot, isTournamentOwnedByToc } from './tocOwnership';
import { findPendingEndOfRoundRoundIndex, isTocRoundPendingAcknowledgement } from './tocPendingRound';
import { type TournamentOfChampions, TournamentOfChampionsState, type TournamentV2Response } from './types';

// Per-device record of later rounds whose leaderboard already auto-opened, keyed by player too.
const roundStartSeen = createSeenList('toc-round-start-seen');
const roundStartSeenKey = (playerId: number | string, tournamentKey: string) => `${playerId}:${tournamentKey}`;

// A lobby clear this long with the dialog sequence still not 'OFF' counts as a stalled sequence.
export const TOC_AUTO_OPEN_SEQUENCE_FALLBACK_MS = 5000;

export class TournamentOfChampionsStore {
  private readonly rootStore: RootStore;

  // Banner URL currently in the rive preload list, swapped when the ToC data or a load failure resolves another.
  private preloadedBannerRiveUrl: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    // Each dialog open refreshes stale data (a round start may have moved) and retries a banner file that failed earlier.
    reaction(
      () => this.rootStore.uiStore?.openDialog.dialog === EnumOpenDialog.isOpenTournamentDialog,
      (isOpen) => {
        if (!isOpen) {
          cancelTocDialogOpen();
          return;
        }
        if (this.shouldRenderDialog) this.markDialogOpenRequested();
        this.clearFailedBannerRives();
        if (this.tournamentOfChampions) void this.refreshStaleData();
      },
    );

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible' || !this.tournamentOfChampions) return;
      void this.refreshStaleData();
    });
  }

  // Data fetched before a round started or ended is stale however young it is.
  private get maxDataAgeMs(): number {
    if (this.isCountdownBehind) return 0;
    const now = Date.now();
    const sinceEachBoundary = (this.tournamentOfChampions?.rounds ?? [])
      .flatMap((round) => [round.startDate, round.endDate])
      .map((date) => (date ? now - dayjs(date).valueOf() : NaN))
      .filter((elapsedMs) => elapsedMs >= 0);
    return Math.min(TOC_DATA_MAX_AGE_MS, ...sinceEachBoundary);
  }

  private markDialogOpenRequested() {
    const { coinType } = this.rootStore.coinTypeStore;
    const coin = coinType.toLowerCase() as 'gc' | 'sc';
    const shownBy = this.rootStore.uiStore.openDialog.options?.shown_by;
    markTocDialogOpenRequested({
      coinType,
      championsKey: this.tournamentOfChampions?.championsKey ?? '',
      tocState: this.tournamentOfChampions?.state ?? '',
      roundIndex: this.tournamentOfChampions?.currentRoundIndex ?? -1,
      leaderboardRows: this.rootStore.tournamentStore.currentTournament?.leaderboards[coin]?.rankings?.length ?? 0,
      shownBy: typeof shownBy === 'string' ? shownBy : '',
    });
  }

  public refreshStaleData(): Promise<FeatureFetchStatus> {
    return this.rootStore.tournamentStore.fetch(undefined, { maxAgeMs: this.maxDataAgeMs });
  }

  // The backend can lag a round boundary: its data still counts down to a start that has passed.
  private get isCountdownBehind(): boolean {
    return this.isInCountdownState && this.isDateInPast(this.rawCountdownEndDate);
  }

  private staleCountdownRetries = 0;
  private staleCountdownTimer: ReturnType<typeof setTimeout> | undefined;

  private clearStaleCountdownRetry() {
    clearTimeout(this.staleCountdownTimer);
    this.staleCountdownTimer = undefined;
  }

  // Refetches with backoff while the data lags a boundary, instead of leaving the old round up until the next poll.
  private retryWhileCountdownBehind() {
    this.clearStaleCountdownRetry();
    if (!this.isCountdownBehind) {
      this.staleCountdownRetries = 0;
      return;
    }
    if (this.staleCountdownRetries >= TOC_STALE_COUNTDOWN_MAX_RETRIES) return;
    const delayMs = Math.min(
      TOC_STALE_COUNTDOWN_RETRY_MS * 2 ** this.staleCountdownRetries,
      TOC_STALE_COUNTDOWN_MAX_RETRY_MS,
    );
    this.staleCountdownRetries += 1;
    this.staleCountdownTimer = setTimeout(() => {
      if (!document.hidden) void this.refreshStaleData();
    }, delayMs);
  }

  @observable
  public tournamentOfChampions: TournamentOfChampions | null = null;

  // The coin whose fetch set `tournamentOfChampions`: a coin switch keeps the old coin's data until its own fetch lands.
  @observable
  public tocCoinType: CoinType | null = null;

  @observable
  public endOfRoundDismissedForRound: number | null = null;

  @observable
  public eliminationComplete = false;

  // championsKey whose champion Summary was finished this session; it must not auto-open again.
  @observable
  private summaryDismissedChampionsKey: string | null = null;

  @computed
  public get isChampionsSummaryDismissed(): boolean {
    const key = this.tournamentOfChampions?.championsKey;
    return !!key && key === this.summaryDismissedChampionsKey;
  }

  @computed
  public get state(): TournamentOfChampionsState | null {
    return this.tournamentOfChampions?.state ?? null;
  }

  @computed
  public get isPreStart(): boolean {
    return this.tournamentOfChampions?.state === TournamentOfChampionsState.PRE_CHAMPIONS;
  }

  @computed
  public get isActiveRound(): boolean {
    return this.tournamentOfChampions?.state === TournamentOfChampionsState.ACTIVE_ROUND;
  }

  @computed
  public get currentRound() {
    if (!this.tournamentOfChampions) return null;
    return (
      this.tournamentOfChampions.rounds.find((r) => r.roundIndex === this.tournamentOfChampions?.currentRoundIndex) ??
      null
    );
  }

  @computed
  public get nextRound() {
    return this.tournamentOfChampions?.rounds.find((r) => !r.isFinalized) ?? null;
  }

  @computed
  public get isBetweenRounds(): boolean {
    return (
      this.tournamentOfChampions?.state === TournamentOfChampionsState.BETWEEN_ROUNDS_QUALIFIED ||
      this.tournamentOfChampions?.state === TournamentOfChampionsState.BETWEEN_ROUNDS_ELIMINATED
    );
  }

  @computed
  public get isChampionsCompleted(): boolean {
    return this.tournamentOfChampions?.state === TournamentOfChampionsState.CHAMPIONS_COMPLETED;
  }

  @computed
  public get isSummaryWidget(): boolean {
    const toc = this.tournamentOfChampions;
    if (!toc || this.eliminationComplete) return false;
    return shouldShowEliminationSummary(toc);
  }

  // Gated on slot ownership like `shouldRenderDialog`: a player seated in an unrelated regular
  // tournament must see that race's widget (position + timer), not TOC's, which would open the
  // regular dialog anyway — unless TOC still owes them a round result (`doesTocHoldTournamentSlot`).
  @computed
  public get shouldShowWidget(): boolean {
    if (!this.tournamentOfChampions) return false;
    if (this.eliminationComplete) return false;
    if (!this.ownsCurrentTournamentSlot) return false;
    if (this.isChampionsCompleted) return false;
    if (this.hasPendingPopup) return true;
    return this.isPreStart || this.isActiveRound || this.isBetweenRounds;
  }

  // Is the seated tournament one of this bracket's rounds? Null (nothing seated) counts. Narrower
  // than `ownsCurrentTournamentSlot`: only then is `currentTournament` TOC's own round data.
  @computed
  public get isSeatedInTocRound(): boolean {
    const currentTournament = this.rootStore.tournamentStore.currentTournament;
    if (!currentTournament) return true;
    return isTournamentOwnedByToc(currentTournament, this.tournamentOfChampions);
  }

  // Does TOC hold the tournament slot over whatever is seated? See `doesTocHoldTournamentSlot`.
  @computed
  public get ownsCurrentTournamentSlot(): boolean {
    return doesTocHoldTournamentSlot({
      currentTournament: this.rootStore.tournamentStore.currentTournament,
      tournamentOfChampions: this.tournamentOfChampions,
      hasPendingPopup: this.hasPendingPopup,
    });
  }

  // A TOC payload that holds the slot: the regular race widget must stay out of it.
  @computed
  public get holdsTournamentSlot(): boolean {
    return !!this.tournamentOfChampions && this.ownsCurrentTournamentSlot;
  }

  @computed
  public get shouldRenderDialog(): boolean {
    return !!this.tournamentOfChampions && !this.eliminationComplete && this.ownsCurrentTournamentSlot;
  }

  @computed
  public get isEliminated(): boolean {
    return this.tournamentOfChampions?.isEliminated === true;
  }

  @computed
  public get hasPendingRoundAcknowledgement(): boolean {
    return this.tournamentOfChampions?.rounds.some((r) => isTocRoundPendingAcknowledgement(r)) ?? false;
  }

  @computed
  public get canOpenBetweenRoundsInfo(): boolean {
    return this.isBetweenRounds && !this.isEliminated;
  }

  @computed
  public get canOpenDialog(): boolean {
    return (
      this.isPreStart ||
      this.isActiveRound ||
      this.hasPendingRoundAcknowledgement ||
      this.canOpenBetweenRoundsInfo ||
      this.hasPendingPopup ||
      this.isChampionsCompleted
    );
  }

  @computed
  public get nextRoundStartDate(): string | null {
    return this.nextRound?.startDate ?? null;
  }

  @computed
  public get firstRoundStartDate(): string | null {
    return this.tournamentOfChampions?.rounds[0]?.startDate ?? null;
  }

  @computed
  public get isInCountdownState(): boolean {
    return this.isPreStart || (this.isBetweenRounds && !this.isEliminated);
  }

  private get rawCountdownEndDate(): string | null {
    if (!this.isInCountdownState) return null;
    if (this.isPreStart) return this.firstRoundStartDate;
    return this.nextRoundStartDate;
  }

  private isDateInPast(iso: string | null | undefined): boolean {
    if (!iso) return false;
    const parsed = dayjs(iso);
    if (!parsed.isValid()) return false;
    return parsed.isBefore(dayjs());
  }

  @computed
  public get countdownEndDate(): string | null {
    const raw = this.rawCountdownEndDate;
    if (this.isDateInPast(raw)) return null;
    return raw;
  }

  @computed
  public get isCountdownToNextRound(): boolean {
    return this.countdownEndDate != null;
  }

  @computed
  public get isOnFinalRound(): boolean {
    const toc = this.tournamentOfChampions;
    if (!toc) return false;
    return this.isActiveRound && toc.currentRoundIndex === toc.rounds.length - 1;
  }

  private get hasUndismissedEndOfRoundClaim(): boolean {
    const toc = this.tournamentOfChampions;
    if (!toc) return false;
    const pendingEndOfRoundIndex = findPendingEndOfRoundRoundIndex(toc.rounds);
    return pendingEndOfRoundIndex >= 0 && this.endOfRoundDismissedForRound !== pendingEndOfRoundIndex;
  }

  private get hasUndismissedEliminationSummary(): boolean {
    const toc = this.tournamentOfChampions;
    if (!toc) return false;
    return toc.isEliminated === true && shouldShowEliminationSummary(toc) && !this.eliminationComplete;
  }

  @computed
  public get hasPendingPopup(): boolean {
    return this.hasUndismissedEndOfRoundClaim || this.hasUndismissedEliminationSummary;
  }

  @computed
  public get widgetIcon(): string | null {
    if (!this.tournamentOfChampions) return null;
    if (this.eliminationComplete) return null;

    if (this.isInCountdownState) {
      return DEFAULT_STARTS_IN_TROPHY;
    }

    if (this.isActiveRound) {
      const roundIndex = this.tournamentOfChampions.currentRoundIndex;
      return (
        this.currentRound?.cupActiveImg ?? getDefaultTocTrophy(roundIndex, this.tournamentOfChampions.rounds.length)
      );
    }

    if (this.isBetweenRounds || this.isSummaryWidget) {
      const nextRound = this.nextRound;
      return (
        nextRound?.cupActiveImg ??
        this.tournamentOfChampions.assets.betweenRoundsWidgetImg ??
        DEFAULT_BETWEEN_ROUND_TROPHY
      );
    }

    return null;
  }

  @action
  public setTocData(tournamentOfChampions: TournamentOfChampions | null, coinType: CoinType | null = null) {
    const isSameChampions =
      !!tournamentOfChampions && tournamentOfChampions.championsKey === this.tournamentOfChampions?.championsKey;

    if (!tournamentOfChampions || !isSameChampions) {
      this.endOfRoundDismissedForRound = null;
      this.eliminationComplete = false;
      this.summaryDismissedChampionsKey = null;
    }

    this.tournamentOfChampions = tournamentOfChampions;
    this.tocCoinType = tournamentOfChampions ? coinType : null;

    if (tournamentOfChampions) {
      this.preloadLeaderboardAssets(tournamentOfChampions);
    }

    if (tournamentOfChampions && tournamentOfChampions.rounds.length > 0) {
      const pendingEndOfRoundIndex = findPendingEndOfRoundRoundIndex(tournamentOfChampions.rounds);
      const hasUnacknowledgedParticipation = pendingEndOfRoundIndex >= 0;
      // A round this session claimed stays dismissed while the backend still reports it pending.
      const claimedThisSession = this.claimedRoundKeys.has(
        `${tournamentOfChampions.championsKey}:${pendingEndOfRoundIndex}`,
      );
      if (hasUnacknowledgedParticipation && !claimedThisSession) {
        if (this.endOfRoundDismissedForRound === pendingEndOfRoundIndex) {
          this.endOfRoundDismissedForRound = null;
        }
      }
    }

    if (resolveTocMock() && TOC_MOCK_ACKNOWLEDGED) {
      if (TOC_MOCK_ACKNOWLEDGED.dismissedRound !== undefined) {
        this.endOfRoundDismissedForRound = TOC_MOCK_ACKNOWLEDGED.dismissedRound;
      }
      if (TOC_MOCK_ACKNOWLEDGED.eliminationComplete) {
        this.eliminationComplete = true;
      }
    }

    this.retryWhileCountdownBehind();

    if (
      tournamentOfChampions &&
      tournamentOfChampions.state === TournamentOfChampionsState.CHAMPIONS_COMPLETED &&
      !tournamentOfChampions.isEliminated &&
      !this.isChampionsSummaryDismissed
    ) {
      if (this.isSettledOneRoundChampion) void this.dismissSummarySilently(tournamentOfChampions.championsKey);
      else if (!this.isPendingRoundAutoOpenHeld) {
        this.rootStore.featureManagerStore.addDialogsToSequenceQueue(['PostTournament']);
      }
    }
  }

  // Completed one-round champion with its round claimed: no Summary to show, so the dialog has nothing to render.
  @computed
  public get isSettledOneRoundChampion(): boolean {
    const toc = this.tournamentOfChampions;
    if (!toc || toc.state !== TournamentOfChampionsState.CHAMPIONS_COMPLETED || toc.isEliminated) return false;
    return toc.rounds.length <= 1 && findPendingEndOfRoundRoundIndex(toc.rounds) < 0;
  }

  // Dismisses without opening anything or resuming the login queue.
  private async dismissSummarySilently(championsKey: string) {
    if (!championsKey) return;
    this.dismissChampionsSummary(championsKey);
    try {
      const coinType = this.rootStore.coinTypeStore.coinType.toLowerCase() as 'gc' | 'sc';
      await postRequest(`tournament/champions/dismiss-summary/${championsKey}/${coinType}`);
    } catch (e) {
      devError('dismissSummarySilently failed', e);
    }
  }

  // Banner .riv URLs that failed since the dialog last opened: the banner and the preload use the bundled default.
  public readonly failedBannerRiveUrls = observable.set<string>();

  @action
  public markBannerRiveFailed(url: string) {
    if (this.failedBannerRiveUrls.has(url)) return;
    this.failedBannerRiveUrls.add(url);
    this.refreshBannerRivePreload();
  }

  @action
  private clearFailedBannerRives() {
    if (this.failedBannerRiveUrls.size === 0) return;
    this.failedBannerRiveUrls.clear();
    this.refreshBannerRivePreload();
  }

  private preloadLeaderboardAssets(tournamentOfChampions: TournamentOfChampions) {
    const url = resolveTocBannerRiveUrl(tournamentOfChampions.assets, this.failedBannerRiveUrls);
    if (url === this.preloadedBannerRiveUrl) return;

    const { riveFileStore } = this.rootStore;
    if (this.preloadedBannerRiveUrl) riveFileStore.removeFromPreload(this.preloadedBannerRiveUrl);
    riveFileStore.addFileToPreload(url);
    this.preloadedBannerRiveUrl = url;
  }

  private readonly refreshBannerRivePreload = () => {
    if (this.tournamentOfChampions) this.preloadLeaderboardAssets(this.tournamentOfChampions);
  };

  @action
  public reset() {
    this.disposeAutoOpenRetry();
    this.clearStaleCountdownRetry();
    this.staleCountdownRetries = 0;
    this.autoOpenHeldRoundKey = null;
    this.claimedRoundKeys.clear();
    this.tournamentOfChampions = null;
    this.tocCoinType = null;
    this.endOfRoundDismissedForRound = null;
    this.eliminationComplete = false;
    this.summaryDismissedChampionsKey = null;
  }

  @action
  public dismissChampionsSummary(championsKey: string) {
    this.summaryDismissedChampionsKey = championsKey;
  }

  @action
  public dismissEndOfRound(roundIndex: number) {
    this.endOfRoundDismissedForRound = roundIndex;
  }

  @action
  public completeElimination() {
    this.eliminationComplete = true;
  }

  public async finishChampionsForPlayer(championsKey: string) {
    if (!championsKey) return;
    // Before the POST: a queued PostTournament step must not reopen the Summary meanwhile.
    this.dismissChampionsSummary(championsKey);
    try {
      const coinType = this.rootStore.coinTypeStore.coinType.toLowerCase() as 'gc' | 'sc';
      await postRequest(`tournament/champions/dismiss-summary/${championsKey}/${coinType}`);
    } catch (e) {
      devError('finishChampionsForPlayer failed', e);
    }

    this.rootStore.featureManagerStore.onDialogFinished('PostTournament');

    try {
      await this.rootStore.tournamentStore.fetch();
    } catch (e) {
      devError('finishChampionsForPlayer refetch failed', e);
    }
  }

  public getMockFetchPayload(): TournamentV2Response['tournaments'] | null {
    const tocMock = resolveTocMock();
    if (!tocMock) return null;
    const fresh = tocMock().tournaments;
    const live = this.tournamentOfChampions;
    if (!live || !fresh.tournamentOfChampions || live.championsKey !== fresh.tournamentOfChampions.championsKey) {
      return fresh;
    }

    const mergedRounds = fresh.tournamentOfChampions.rounds.map((round, index) => ({
      ...round,
      roundAcknowledged: live.rounds[index]?.roundAcknowledged ?? round.roundAcknowledged,
    }));

    return {
      ...fresh,
      tournamentOfChampions: {
        ...fresh.tournamentOfChampions,
        rounds: mergedRounds,
      },
    };
  }

  public normalizeV2TournamentsPayload(data: TournamentV2Response['tournaments']): TournamentV2Response['tournaments'] {
    if (!data.tournamentOfChampions) return data;
    return {
      ...data,
      tournamentOfChampions: this.normalize(data.tournamentOfChampions),
    };
  }

  // Round finishes still in flight (one can outlive the popup's 10 s claim timeout).
  private readonly finishingRoundKeys = new Set<string>();

  // `championsKey:roundIndex` of rounds claimed this session; a lagging fetch must not pop them again.
  private readonly claimedRoundKeys = new Set<string>();

  private markRoundClaimed(tournamentKey: string) {
    const toc = this.tournamentOfChampions;
    if (!toc) return;
    const byKey = toc.rounds.findIndex((r) => r.playerTournamentKey === tournamentKey);
    const roundIndex = byKey >= 0 ? byKey : findPendingEndOfRoundRoundIndex(toc.rounds);
    if (roundIndex >= 0) this.claimedRoundKeys.add(`${toc.championsKey}:${roundIndex}`);
  }

  public async finishRoundForPlayer(tournamentKey: string | undefined, options?: { deferFetch?: boolean }) {
    if (!tournamentKey) {
      devError('[TOC] No tournament key for round finish');
      return;
    }
    if (this.finishingRoundKeys.has(tournamentKey)) {
      throw new Error('[TOC] Round finish already in flight');
    }

    const loadingKey = 'finishTocRoundForPlayer';
    this.finishingRoundKeys.add(tournamentKey);
    this.rootStore.tournamentStore.setLoading(loadingKey, true);
    try {
      if (resolveTocMock()) {
        this.acknowledgeMockRoundInPlace(tournamentKey);
        if (!options?.deferFetch) {
          await this.rootStore.tournamentStore.fetch(undefined, { force: true });
        }
        return;
      }

      const coinType = this.rootStore.coinTypeStore.coinType.toLowerCase() as 'gc' | 'sc';
      await postRequest(`tournament/finish/${tournamentKey}/${coinType}`);
      this.markRoundClaimed(tournamentKey);
      if (!options?.deferFetch) {
        await this.rootStore.tournamentStore.fetch(undefined, { force: true });
      }
    } finally {
      this.finishingRoundKeys.delete(tournamentKey);
      this.rootStore.tournamentStore.setLoading(loadingKey, false);
    }
  }

  public get isFetchReadyWithTocOnly(): boolean {
    return !!this.tournamentOfChampions;
  }

  @action
  private acknowledgeMockRoundInPlace(tournamentKey: string) {
    const rounds = this.tournamentOfChampions?.rounds;
    if (!rounds?.length) return;

    const round =
      rounds.find((r) => r.playerTournamentKey === tournamentKey) ??
      rounds.find((r) => r.isFinalized && r.roundAcknowledged !== true);
    if (!round) return;

    round.roundAcknowledged = true;
  }

  // Fetch-driven auto-opens wait for the lobby: never over a dialog, full-page dialog, portal, warm frame or game.
  private get canAutoOpenFromFetch(): boolean {
    const { uiStore, gameStore, metagamesWarmStore, portalStore } = this.rootStore;
    return (
      uiStore.openDialog.dialog == null &&
      !uiStore.openFullPageDialog &&
      !portalStore.isVisible &&
      !metagamesWarmStore.isVisible &&
      !gameStore.gameId
    );
  }

  // Auto-opens a blocker (e.g. the jackpot jump) deferred; re-run once the lobby is clear, not on a later fetch.
  private readonly deferredAutoOpens = new Set<'pendingPopup' | 'roundStart'>();
  private autoOpenRetryDisposer: (() => void) | null = null;

  // The feature manager opens its next queued dialog ~300 ms after one closes; 'OFF' means nothing more is coming.
  private get isDialogSequenceSettled(): boolean {
    return this.rootStore.featureManagerStore.sequenceState === 'OFF';
  }

  private get isTocDialogOpen(): boolean {
    return this.rootStore.uiStore.openDialog.dialog === EnumOpenDialog.isOpenTournamentDialog;
  }

  private disposeAutoOpenRetry() {
    this.autoOpenRetryDisposer?.();
    this.autoOpenRetryDisposer = null;
    this.deferredAutoOpens.clear();
  }

  private retryAutoOpenWhenClear(check: 'pendingPopup' | 'roundStart') {
    // The TOC dialog closing must not reopen itself.
    if (this.isTocDialogOpen) return;
    this.deferredAutoOpens.add(check);
    if (this.autoOpenRetryDisposer) return;
    let sequenceFallback: ReturnType<typeof setTimeout> | undefined;
    const stopWatching = reaction(
      () => {
        if (this.isTocDialogOpen || (this.canAutoOpenFromFetch && this.isDialogSequenceSettled)) return 'run';
        return this.canAutoOpenFromFetch ? 'awaitSequence' : 'blocked';
      },
      (status) => {
        clearTimeout(sequenceFallback);
        if (status === 'run') this.runDeferredAutoOpens();
        // A dialog closed without onDialogFinished leaves the sequence stuck; retry once the lobby stays clear.
        else if (status === 'awaitSequence') {
          sequenceFallback = setTimeout(() => this.runDeferredAutoOpens(), TOC_AUTO_OPEN_SEQUENCE_FALLBACK_MS);
        }
      },
    );
    this.autoOpenRetryDisposer = () => {
      clearTimeout(sequenceFallback);
      stopWatching();
    };
  }

  private runDeferredAutoOpens() {
    const deferred = new Set(this.deferredAutoOpens);
    this.disposeAutoOpenRetry();
    // The TOC dialog opened meanwhile: its own close must not reopen it.
    if (this.isTocDialogOpen) return;
    if (deferred.has('pendingPopup')) this.checkAutoOpenPendingPopup();
    if (deferred.has('roundStart')) this.checkAutoOpenRoundStart();
  }

  // `championsKey:roundIndex` of a round whose claim aborted; fetch auto-opens skip it until a lobby fetch.
  private autoOpenHeldRoundKey: string | null = null;

  // After a failed / timed-out claim: the refetch must not reopen that round's popup at once.
  public holdEndOfRoundAutoOpen(roundIndex: number) {
    // -1 would match "nothing pending" and block the champion Summary.
    if (roundIndex < 0) return;
    const championsKey = this.tournamentOfChampions?.championsKey;
    this.autoOpenHeldRoundKey = championsKey ? `${championsKey}:${roundIndex}` : null;
  }

  public get isPendingRoundAutoOpenHeld(): boolean {
    const toc = this.tournamentOfChampions;
    if (!toc || !this.autoOpenHeldRoundKey) return false;
    return this.autoOpenHeldRoundKey === `${toc.championsKey}:${findPendingEndOfRoundRoundIndex(toc.rounds)}`;
  }

  // Called by TournamentStore.performFetch, once its own currentTournament reflects the same
  // fetch's response — not from setTocData directly, which runs first and would otherwise read
  // a stale currentTournament left over from the previous cycle (see ownsCurrentTournamentSlot).
  public checkAutoOpenPendingPopup(options?: { lobbyFetch?: boolean }) {
    // A lobby fetch (login, back to lobby, coin switch) lets a held round pop again.
    if (options?.lobbyFetch) this.autoOpenHeldRoundKey = null;
    if (this.isPendingRoundAutoOpenHeld) return;
    if (!this.canAutoOpenFromFetch) {
      this.retryAutoOpenWhenClear('pendingPopup');
      return;
    }
    // Was previously suppressed while viewing GC — but TOC round acknowledgement isn't tied to
    // coin type, and skipping it here left a player permanently stuck if they only ever check
    // GC: nothing else prompts them to acknowledge a finished round, and a new TOC can't start
    // for them until they do.
    const shouldOpen = this.hasUndismissedEliminationSummary || this.hasUndismissedEndOfRoundClaim;
    if (!shouldOpen) return;
    // Content selection (selectTournamentDialogVariant) only renders TOC when it owns the dialog
    // slot — opening here regardless would surface an unrelated, unclaimed regular tournament's
    // dialog instead (an unintended auto-open that bypasses that tournament's own gating). Defer;
    // this re-runs on every fetch, so it fires once TOC actually owns the slot again.
    if (!this.ownsCurrentTournamentSlot) return;

    this.rootStore.uiStore.setOpenDialog({
      dialog: EnumOpenDialog.isOpenTournamentDialog,
      options: { shown_by: 'log_in' },
    });
  }

  @computed
  public get isInLaterRound(): boolean {
    return (this.tournamentOfChampions?.currentRoundIndex ?? 0) > 0 && this.isSeatedInTocRound;
  }

  // The current round's bracket key, for rounds after the first (round 1 opens through the kickoff).
  private get laterRoundStartKey(): string | null {
    if (!this.isInLaterRound || !this.currentRound) return null;
    return this.currentRound.playerTournamentKey ?? this.rootStore.tournamentStore.currentTournament?.key ?? null;
  }

  // A popup must show before the live round; the end of round this session claimed (backend lag) does not count.
  private hasBlockingPopup(claimedRoundIndex?: number): boolean {
    if (this.hasUndismissedEliminationSummary) return true;
    if (!this.hasUndismissedEndOfRoundClaim) return false;
    return claimedRoundIndex === undefined || this.pendingEndOfRoundIndex !== claimedRoundIndex;
  }

  private get pendingEndOfRoundIndex(): number {
    return findPendingEndOfRoundRoundIndex(this.tournamentOfChampions?.rounds);
  }

  // The player's current round is live: active, started, not finalized, nothing left to acknowledge.
  private isCurrentRoundLiveForPlayer(claimedRoundIndex?: number): boolean {
    const round = this.currentRound;
    if (!round) return false;
    if (!this.isActiveRound || this.isEliminated || this.hasBlockingPopup(claimedRoundIndex)) return false;
    return !round.isFinalized && this.isDateInPast(round.startDate);
  }

  // currentTournament is the live round's bracket (it lags or stays null while the backend still says upcoming).
  private get isLiveRoundTournamentLoaded(): boolean {
    const round = this.currentRound;
    const tournament = this.rootStore.tournamentStore.currentTournament;
    if (!round || !tournament || tournament.tournamentGroupKey !== round.tournamentGroupKey) return false;
    return !round.playerTournamentKey || tournament.key === round.playerTournamentKey;
  }

  // On SC, auto-open the first time the player is in a started round after round 1.
  private get shouldAutoOpenRoundStart(): boolean {
    const playerId = this.rootStore.userStore.profile?.userId;
    const tournamentKey = this.laterRoundStartKey;
    if (playerId == null || !tournamentKey) return false;
    if (this.rootStore.coinTypeStore.coinType !== 'SC') return false;
    if (!this.isCurrentRoundLiveForPlayer()) return false;
    return !roundStartSeen.read().includes(roundStartSeenKey(playerId, tournamentKey));
  }

  /** Set by the round-start auto-open; the dialog shows the between-rounds popup before the leaderboard. */
  @observable
  private roundStartPopupPending = false;

  /**
   * The open TOC dialog came from the round-start auto-open and still shows its between-rounds popup (progress map
   * IDLE, live). Tied to that dialog session: any other open of the dialog goes straight to the leaderboard.
   */
  @computed
  public get isRoundStartPopupShowing(): boolean {
    if (!this.roundStartPopupPending) return false;
    const { openDialog } = this.rootStore.uiStore;
    return (
      openDialog.dialog === EnumOpenDialog.isOpenTournamentDialog && openDialog.options?.shown_by === 'round_start'
    );
  }

  /** The round-start popup closed: the same dialog moves on to the current round's leaderboard. */
  @action
  public dismissRoundStartPopup() {
    this.roundStartPopupPending = false;
  }

  // Opens the started round in the TOC dialog and uses up that round's auto-open, so it does not open again.
  @action
  private openRoundStartDialog(withPopup: boolean) {
    const playerId = this.rootStore.userStore.profile?.userId;
    const tournamentKey = this.laterRoundStartKey;
    if (playerId != null && tournamentKey) roundStartSeen.mark(roundStartSeenKey(playerId, tournamentKey));
    this.roundStartPopupPending = withPopup;
    this.rootStore.uiStore.setOpenDialog({
      dialog: EnumOpenDialog.isOpenTournamentDialog,
      options: { shown_by: 'round_start' },
    });
  }

  // Live-round landing can still succeed: not eliminated, no popup first (except the round this session claimed).
  public canLandOnLiveRound(claimedRoundIndex?: number): boolean {
    if (!this.tournamentOfChampions || this.isEliminated) return false;
    return !this.hasBlockingPopup(claimedRoundIndex);
  }

  /** Opens the between-rounds popup, then the leaderboard, once per later round; returns whether it did. */
  public tryAutoOpenRoundStart(): boolean {
    if (!this.shouldAutoOpenRoundStart) return false;
    this.openRoundStartDialog(true);
    return true;
  }

  /**
   * Progress-map CTA when the upcoming round already started: lands the open dialog on that round's leaderboard
   * (the round-start path without its popup, on either coin and even if it auto-opened before). Call it after a
   * refetch; returns false, opening nothing, while the data does not show a live round yet. `claimedRoundIndex` as
   * in `canLandOnLiveRound`.
   */
  public openLiveRoundLeaderboard(claimedRoundIndex?: number): boolean {
    // A refetch that lands after the player closed the dialog must not reopen it.
    if (!this.isTocDialogOpen) return false;
    if (!this.isCurrentRoundLiveForPlayer(claimedRoundIndex) || !this.isLiveRoundTournamentLoaded) return false;
    this.openRoundStartDialog(false);
    return true;
  }

  // Mid-session path: a fetch (e.g. the widget's countdown ending) lands after the round started.
  public checkAutoOpenRoundStart() {
    if (!this.canAutoOpenFromFetch) {
      this.retryAutoOpenWhenClear('roundStart');
      return;
    }
    this.tryAutoOpenRoundStart();
  }

  public normalize(raw: any): TournamentOfChampions {
    const normalizePrizePool = (pool: any) => {
      if (isNil(pool)) return null;
      const toNum = (v: unknown): number => {
        if (isNil(v)) return 0;
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
      };

      const hasGc = Object.prototype.hasOwnProperty.call(pool, 'gc');
      const hasSc = Object.prototype.hasOwnProperty.call(pool, 'sc');
      if (hasGc || hasSc) {
        return { gcCoin: toNum(pool.gc), scCoin: toNum(pool.sc) };
      }
      return { gcCoin: toNum(pool.gcCoin), scCoin: toNum(pool.scCoin) };
    };

    return {
      ...raw,
      assets: {
        bannerBackgroundImg: raw.assets?.bannerBackgroundImg ?? null,
        bannerAnimation: raw.assets?.bannerAnimation ?? null,
        summaryAnimation: raw.assets?.summaryAnimation ?? null,
        betweenRoundsWidgetImg: raw.assets?.betweenRoundsWidgetImg ?? null,
        introAnimation: raw.assets?.introAnimation ?? null,
        endOfRoundAnimation: raw.assets?.endOfRoundAnimation ?? null,
        betweenRoundsAnimation: raw.assets?.betweenRoundsAnimation ?? null,
        shareBackgroundImg: raw.assets?.shareBackgroundImg ?? null,
        mainProgressMapAnimation: raw.assets?.mainProgressMapAnimation ?? null,
        personalExperienceAnimation: raw.assets?.personalExperienceAnimation ?? null,
        summary: raw.assets?.summary ?? null,
        bannerAndTopbarAnimation: raw.assets?.bannerAndTopbarAnimation ?? null,
        // game-ui reads its scene files from here too.
        rives: raw.assets?.rives ?? {},
      },

      rounds: raw.rounds.map((round: any) => ({
        ...round,
        cupActiveImg: round.cupActiveImg ?? null,
        cupInactiveImg: round.cupInactiveImg ?? null,
        widgetDesignImg: round.widgetDesignImg ?? null,
        playersToAdvanceLimit: round.playersToAdvanceLimit ?? null,
        prizePool: normalizePrizePool(round.prizePool),
      })),
    };
  }
}
